import {CancelToken} from 'axios';
import {FeedItem} from './feedItems.types';
import {FeedRelatedItemRelationType} from '../relatedItems/relatedItems.types';
import apiV2Service from '../api/api-v2.service';
import {Pagination, ResponseMetaData} from '../api/apiTypes';
import waitForJob from '../jobs/utils/waitForJob';
import {JobExecutionParams} from '../jobs/jobs.types';
import {SelectedItemMetadata} from '../../components/CLSelectedItems/CLSelectedItems';
import {toLocalFeedItem} from './feedItems.dataConverters';
import {TaxonomyType} from '../taxonomy/taxonomy.types';

const basePath = `FeedItems`;

export interface GetFeedItemsProps {
  feedId: number;
  logicsId?: number;
  categoryId?: string;
  searchTerm?: string;
  orderBy?: string;
  orderByDesc?: boolean;
  isGetTotal?: boolean;
  index?: number;
  count?: number;
  cancelToken?: CancelToken;
  relationType?: FeedRelatedItemRelationType;
  showOnlyInvalidItems?: boolean;
}

export interface GetProductResponse {
  items: FeedItem[];
  pagination: Pagination;
  meta: ResponseMetaData;
}

async function getFeedItems({
  feedId,
  logicsId,
  categoryId,
  searchTerm,
  orderBy,
  orderByDesc,
  isGetTotal,
  index,
  count,
  cancelToken,
  relationType,
  showOnlyInvalidItems,
}: GetFeedItemsProps): Promise<GetProductResponse> {
  const params = apiV2Service.cleanEmptyValues({
    feedId,
    logicsId,
    categoryId,
    orderBy,
    orderByDesc,
    isGetTotal,
    index,
    count,
    searchTerm,
    relationType,
    showOnlyInvalidItems,
  });

  const {data: response} = await apiV2Service.executeGetRequest<GetProductResponse>(
    `${basePath}/GetProducts`,
    params,
    {
      cancelToken,
    },
  );

  return {...response, items: response.items.map(toLocalFeedItem)};
}

async function getTitles(feedId: number, productIds: string[]) {
  const {data: items} = await apiV2Service.executePostRequest<SelectedItemMetadata[]>(
    `${basePath}/GetTitles`,
    {},
    {
      feedId,
      productIds: productIds || [],
    },
  );

  return items;
}

async function exportFeedItemsToExcel({
  feedId,
  logicsId,
  categoryId,
  relationType,
  itemIds,
}: {
  feedId: number;
  relationType?: FeedRelatedItemRelationType;
  logicsId?: number;
  categoryId?: string;
  itemIds?: string[];
}) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(
      `${basePath}/${feedId}/export`,
      {feedId},
      {
        ...(logicsId && {logicsId}),
        ...(categoryId && {categoryId}),
        ...(relationType && {relationType}),
        ...(!!itemIds && {selectedItems: itemIds}),
      },
    )
    .then((response) =>
      waitForJob({
        jobId: response.data.executionId,
        jobName: `Export Feed F${feedId} items to excel`,
      }),
    );
}

function getItemsWithIssuesCount(feedId: number, taxonomyType: TaxonomyType) {
  return apiV2Service
    .executeGetRequest<number>(`${basePath}/GetItemsWithIssuesCount`, {
      feedId,
      feedItemType: taxonomyType,
    })
    .then((response) => response.data);
}

async function replaceBgImage(originalImageUrl: string, newImageUrl: string) {
  return apiV2Service.executePostRequest(
    `${basePath}/replaceRemoveBackground`,
    {},
    {
      originalImageUrl,
      newImageUrl,
    },
  );
}

async function getProductsByIds(feedId: number, itemIds: string[]) {
  const {
    data: {items},
  } = await apiV2Service.executePostRequest<{items: FeedItem[]}>(
    `${basePath}/${feedId}/GetByIds`,
    {},
    {itemIds},
  );

  return items;
}

const feedItemsApi = {
  getFeedItems,
  getTitles,
  exportFeedItemsToExcel,
  getItemsWithIssuesCount,
  replaceBgImage,
  getProductsByIds,
};

export default feedItemsApi;
